import { Link } from "react-router-dom";
import {
  Server,
  DollarSign,
  Gavel,
  UserPlus,
  AlertOctagon,
  User,
  type LucideIcon,
} from "lucide-react";
import { format, formatDistanceToNow, parseISO } from "date-fns";
import { es } from "date-fns/locale";
import { cn } from "@/lib/utils/cn";
import type { PulseActivityEntry } from "./types";

const CAT_ICON: Record<PulseActivityEntry["category"], LucideIcon> = {
  system: Server,
  money: DollarSign,
  moderation: Gavel,
  onboarding: UserPlus,
  alert: AlertOctagon,
  user: User,
};

const CAT_COLOR: Record<PulseActivityEntry["category"], string> = {
  system: "text-fg-subtle bg-bg-subtle",
  money: "text-success bg-success-soft",
  moderation: "text-warning bg-warning-soft",
  onboarding: "text-accent bg-accent-soft",
  alert: "text-danger bg-danger-soft",
  user: "text-info bg-info-soft",
};

export function PulseActivityFeed({ entries }: { entries: PulseActivityEntry[] }) {
  if (entries.length === 0) {
    return (
      <div className="text-center text-sm text-fg-subtle py-12">
        Sin actividad reciente.
      </div>
    );
  }
  return (
    <ul className="flex flex-col">
      {entries.map((entry) => {
        const Icon = CAT_ICON[entry.category];
        const colorClass = CAT_COLOR[entry.category];
        // Fecha-hora absoluta (lo que pidió el dueño) + relativa como tooltip.
        const fechaHora = (() => {
          try {
            return format(parseISO(entry.at), "dd/MM/yyyy HH:mm");
          } catch {
            return entry.at;
          }
        })();
        const relativo = (() => {
          try {
            return formatDistanceToNow(parseISO(entry.at), { addSuffix: true, locale: es });
          } catch {
            return "";
          }
        })();
        const Wrapper: any = entry.href ? Link : "div";
        const props = entry.href ? { to: entry.href } : {};
        return (
          <li key={entry.id} className="border-b border-border-subtle last:border-0">
            <Wrapper
              {...props}
              className={cn(
                "flex items-start gap-3 px-3 py-2.5 transition-colors",
                entry.href && "hover:bg-bg-hover cursor-pointer",
              )}
            >
              <div className={cn("shrink-0 w-7 h-7 rounded-md flex items-center justify-center", colorClass)}>
                <Icon size={14} strokeWidth={1.75} />
              </div>
              <div className="flex-1 min-w-0">
                <p className="text-sm text-fg leading-snug">{entry.message}</p>
                <p className="text-2xs text-fg-subtle mt-0.5">
                  <span className="text-fg-muted">{entry.actor}</span> ·{" "}
                  <span title={relativo}>{fechaHora}</span>
                </p>
              </div>
            </Wrapper>
          </li>
        );
      })}
    </ul>
  );
}
